import { format } from 'date-fns';
import { ClockIcon, MapPinIcon, PlayIcon } from '@heroicons/react/24/outline';
import Card from '../../components/ui/Card';
import AddToCalendarButton from '../../components/events/AddToCalendarButton';
import { AppEvent } from '../../types/event';
import { exampleOptions } from './cardReviewStorage';
import { eventScent } from './scentViews';
import { ExampleSwitcher, FamilyBlock, linkDump } from './reviewUi';

function startOf(event: AppEvent) {
  return new Date(event.startAt);
}

function endOf(event: AppEvent) {
  return event.endAt ? new Date(event.endAt) : null;
}

function whenLine(event: AppEvent, past: boolean) {
  const startDate = startOf(event);
  const endDate = endOf(event);
  if (past) return format(startDate, 'MMMM d, yyyy');
  if (event.allDay) return `${format(startDate, 'MMMM d, yyyy')} (All Day)`;
  const end = endDate ? ` - ${format(endDate, 'h:mm a')}` : '';
  return `${format(startDate, 'MMMM d, yyyy')} @ ${format(startDate, 'h:mm a')}${end}`;
}

function calendarParams(event: AppEvent) {
  return {
    title: event.title,
    description: event.excerpt || undefined,
    startDate: startOf(event),
    endDate: endOf(event),
    isAllDay: event.allDay,
    location: event.venue || undefined,
    url: event.websiteUrl || undefined,
  };
}

function AgendaCard({ event, past = false }: { event: AppEvent; past?: boolean }) {
  const startDate = startOf(event);
  return (
    <Card hover className="!p-0">
      <div className={past ? 'p-6' : 'relative p-6'}>
        <div className={past ? 'relative' : undefined}>
          <div className="flex flex-col sm:flex-row gap-4 pr-36 sm:pr-40">
            <div className={`w-20 h-20 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${past ? 'bg-gray-100' : 'bg-primary/10'}`}>
              <span className={`text-2xl font-bold ${past ? 'text-gray-600' : 'text-primary'}`}>{format(startDate, 'd')}</span>
              <span className={`text-xs uppercase ${past ? 'text-gray-500' : 'text-gray-600'}`}>{format(startDate, 'MMM')}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 data-event-title className="text-xl font-semibold text-gray-900 mb-2">{event.title}</h3>
              {event.excerpt && <p className="text-gray-600 mb-3 line-clamp-2">{event.excerpt}</p>}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <ClockIcon className="h-4 w-4" aria-hidden />
                  <span>{whenLine(event, past)}</span>
                </div>
                {event.venue && (
                  <div className="flex items-center gap-1">
                    <MapPinIcon className="h-4 w-4" aria-hidden />
                    <span>{event.venue}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={past ? 'absolute top-0 right-0' : 'absolute top-0 right-0 p-6'}>
            <AddToCalendarButton event={calendarParams(event)} />
          </div>
        </div>
        {past && event.recordingUrl && (
          <div className="flex justify-end mt-4">
            <button type="button" className="inline-flex items-center gap-1 min-h-[44px] text-sm font-medium text-primary">
              <PlayIcon className="h-4 w-4" aria-hidden />
              Watch recording
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

function HomeTile({ event }: { event: AppEvent }) {
  const startDate = startOf(event);
  return (
    <Card hover>
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-lg flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-primary">{format(startDate, 'd')}</span>
          <span className="text-xs text-gray-600 uppercase">{format(startDate, 'MMM')}</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{event.title}</h3>
      </div>
      {event.excerpt && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{event.excerpt}</p>}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        <span>{format(startDate, 'MMM d, yyyy')}</span>
        {event.venue && <span className="truncate">{event.venue}</span>}
      </div>
    </Card>
  );
}

function DateLine({ event }: { event: AppEvent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between gap-4 min-h-[44px] px-4 py-3 text-sm text-gray-900">
        <span className="line-clamp-2">{event.title}</span>
        <span className="text-xs text-gray-500 flex-shrink-0">{format(startOf(event), 'MMM d, yyyy')}</span>
      </div>
    </div>
  );
}

export default function EventReview({
  upcoming,
  past,
  notice,
  error,
  choices,
  onChoose,
  notes,
  onNote,
}: {
  upcoming: AppEvent | null;
  past: AppEvent | null;
  notice: string | null;
  error: string | null;
  choices: Record<string, string>;
  onChoose: (exampleId: string, optionId: string) => void;
  notes: Record<string, string>;
  onNote: (exampleId: string, note: string) => void;
}) {
  const dump = upcoming && linkDump(upcoming.excerpt) ? ' The current excerpt includes a web address.' : '';
  return (
    <FamilyBlock
      id="review-events"
      title="Events"
      judge={`Type rail draws an event as a date. Add to Calendar stays on the events list.${dump}`}
      destination="An event card opens the event."
      notice={notice}
      error={error}
    >
      {upcoming && (
        <>
          <ExampleSwitcher
            exampleId="events-upcoming"
            label="Events list, upcoming"
            options={exampleOptions('events-upcoming')}
            choice={choices['events-upcoming'] ?? ''}
            onChoose={(optionId) => onChoose('events-upcoming', optionId)}
            note={notes['events-upcoming'] ?? ''}
            onNote={(value) => onNote('events-upcoming', value)}
            render={(optionId) =>
              optionId === 'current' ? <AgendaCard event={upcoming} /> : eventScent(upcoming, optionId, "desktop")
            }
          />
          <ExampleSwitcher
            exampleId="events-home"
            label="Public home"
            options={exampleOptions('events-home')}
            choice={choices['events-home'] ?? ''}
            onChoose={(optionId) => onChoose('events-home', optionId)}
            note={notes['events-home'] ?? ''}
            onNote={(value) => onNote('events-home', value)}
            render={(optionId, frame) =>
              optionId === 'current' ? (
                <div className={frame === 'desktop' ? 'max-w-sm' : undefined}>
                  <HomeTile event={upcoming} />
                </div>
              ) : (
                eventScent(upcoming, optionId, frame)
              )
            }
          />
          <ExampleSwitcher
            exampleId="events-practice"
            label="Practice home"
            options={exampleOptions('events-practice')}
            choice={choices['events-practice'] ?? ''}
            onChoose={(optionId) => onChoose('events-practice', optionId)}
            note={notes['events-practice'] ?? ''}
            onNote={(value) => onNote('events-practice', value)}
            render={(optionId) => (optionId === 'current' ? <DateLine event={upcoming} /> : eventScent(upcoming, optionId, "desktop"))}
          />
        </>
      )}
      {past && (
        <ExampleSwitcher
          exampleId="events-past"
          label="Events list, past"
          options={exampleOptions('events-past')}
          choice={choices['events-past'] ?? ''}
          onChoose={(optionId) => onChoose('events-past', optionId)}
          note={notes['events-past'] ?? ''}
          onNote={(value) => onNote('events-past', value)}
          render={(optionId) => (optionId === 'current' ? <AgendaCard event={past} past /> : eventScent(past, optionId, "desktop"))}
        />
      )}
    </FamilyBlock>
  );
}
